import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Skeleton from "@material-ui/lab/Skeleton";
import EmptyState from "../EmptyState";

/**
 * Tabela padrão do produto.
 *
 * Concentra o que estava sendo redecidido a cada página: densidade, cabeçalho
 * fixo, alinhamento numérico, skeleton de carregamento, estado vazio desenhado
 * e rodapé de paginação. As colunas são declaradas como dados, então a página
 * fica com a regra de negócio e não com a marcação da tabela.
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(2, 0, 3),
    overflow: "hidden",
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  table: {
    tableLayout: "auto",
  },
  stickyHead: {
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  clickableRow: {
    cursor: "pointer",
  },
  // Números alinhados em coluna só funcionam com largura de dígito fixa.
  numericCell: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
    whiteSpace: "nowrap",
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  emptyCell: {
    borderBottom: "none",
    padding: 0,
  },
  footer: {
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.paper
        : theme.palette.grey[50],
  },
}));

const DataTable = ({
  columns,
  rows,
  loading,
  getRowKey,
  onRowClick,
  onScroll,
  stickyHeader,
  size,
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyIcon,
  skeletonRows,
  footer,
}) => {
  const classes = useStyles();
  const showEmpty = !loading && rows.length === 0;

  const cellClass = (col) => {
    if (col.type === "numeric") return classes.numericCell;
    if (col.type === "actions") return classes.actionsCell;
    return undefined;
  };

  const cellAlign = (col) => {
    if (col.align) return col.align;
    if (col.type === "numeric" || col.type === "actions") return undefined;
    return "left";
  };

  return (
    <Paper variant="outlined" className={classes.wrapper}>
      <div className={classes.scroll} onScroll={onScroll}>
        <Table size={size} className={classes.table} stickyHeader={stickyHeader}>
          <TableHead className={stickyHeader ? classes.stickyHead : undefined}>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={cellAlign(col)}
                  className={cellClass(col)}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={getRowKey ? getRowKey(row, rowIndex) : row.id ?? rowIndex}
                hover={!!onRowClick}
                className={onRowClick ? classes.clickableRow : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={cellAlign(col)}
                    className={cellClass(col)}
                  >
                    {col.render ? col.render(row, rowIndex) : row[col.key] ?? "—"}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {loading &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((col) => (
                    <TableCell key={col.key} className={cellClass(col)}>
                      <Skeleton animation="wave" height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {showEmpty && (
              <TableRow>
                <TableCell colSpan={columns.length} className={classes.emptyCell}>
                  <EmptyState
                    icon={emptyIcon}
                    title={emptyTitle}
                    description={emptyDescription}
                    action={emptyAction}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {footer && <div className={classes.footer}>{footer}</div>}
    </Paper>
  );
};

DataTable.propTypes = {
  /**
   * Definição das colunas.
   * `type: "numeric"` alinha à direita com dígitos de largura fixa;
   * `type: "actions"` encolhe a coluna e alinha à direita.
   */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      align: PropTypes.oneOf(["left", "center", "right"]),
      type: PropTypes.oneOf(["numeric", "actions"]),
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.array,
  loading: PropTypes.bool,
  getRowKey: PropTypes.func,
  onRowClick: PropTypes.func,
  onScroll: PropTypes.func,
  stickyHeader: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  emptyAction: PropTypes.node,
  emptyIcon: PropTypes.node,
  skeletonRows: PropTypes.number,
  footer: PropTypes.node,
};

DataTable.defaultProps = {
  rows: [],
  loading: false,
  getRowKey: undefined,
  onRowClick: undefined,
  onScroll: undefined,
  stickyHeader: true,
  size: "small",
  emptyTitle: "Nada por aqui ainda",
  emptyDescription: undefined,
  emptyAction: undefined,
  emptyIcon: undefined,
  skeletonRows: 5,
  footer: undefined,
};

export default DataTable;
